import { FC, useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { BrandColorCategory } from "../../../shared/models/brand.model";
import { BrandColorEntry, isBlankHex } from "../hooks/useOrganizationBrand";
import { RequiredFieldValues, removedRequiredFields } from "../hooks/brandRequiredFields";

/*
    The Colors section of the Branding tab behind `brand-identity-v2` (MPN-2027).

    Primary, Secondary and Accent are the legacy color slots: a hex each. Primary and
    Secondary are required (MPN-2174): an unset one shows a hint, and once one is saved
    it can't be cleared, so the save is blocked until it has a color again. Clearing the
    Accent's hex saves it as unset. Every other color is a brand color row with a name, a category and
    guidelines. Legacy values that were never a hex are shown as stored, with a warning.
*/

interface Props {
  colors: BrandColorEntry[];
  savedColors: BrandColorEntry[];
  setColors: (update: (prev: BrandColorEntry[]) => BrandColorEntry[]) => void;
  onSave: () => Promise<void>;
  isLoading: boolean;
}

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const LEGACY_SLOTS: Record<string, { label: string; required: boolean }> = {
  "legacy:primary": { label: "Primary", required: true },
  "legacy:secondary": { label: "Secondary", required: true },
  "legacy:tertiary": { label: "Accent", required: false },
};

// Rows may not be Primary or Secondary while the legacy slots own them.
const ROW_CATEGORIES: { value: BrandColorCategory; label: string }[] = [
  { value: "NEUTRAL", label: "Neutral" },
  { value: "ACCENT", label: "Accent" },
  { value: "ACTION", label: "Action" },
  { value: "TEXT", label: "Text" },
  { value: "OTHER", label: "Other" },
];

// "#" is where an added color starts, so it isn't called out as invalid.
const isInvalidHex = (hex: string) => !isBlankHex(hex) && !HEX_RE.test(hex);

const newColor = (key: string): BrandColorEntry => ({
  key,
  id: "",
  source: "table",
  category: "OTHER",
  hex_code: "#",
  name: null,
  usage_guidelines: null,
  application_guidelines: null,
  is_default: false,
  is_valid_hex: false,
});

// The legacy slots' hexes. A slot not loaded yet isn't judged.
const requiredValues = (colors: BrandColorEntry[]): RequiredFieldValues => ({
  primaryColor: colors.find((c) => c.id === "legacy:primary")?.hex_code,
  secondaryColor: colors.find((c) => c.id === "legacy:secondary")?.hex_code,
});

const BrandColorsSection: FC<Props> = ({ colors, savedColors, setColors, onSave, isLoading }) => {
  const nextKey = useRef(0);
  const legacy = colors.filter((c) => c.source === "legacy");
  const rows = colors.filter((c) => c.source !== "legacy");
  const cleared = removedRequiredFields(requiredValues(savedColors), requiredValues(colors));

  const setField = (key: string, field: keyof BrandColorEntry, value: string) => {
    setColors((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
  };

  const addColor = () => {
    nextKey.current += 1;
    setColors((prev) => [...prev, newColor(`new-${nextKey.current}`)]);
  };

  const removeColor = (key: string) => {
    setColors((prev) => prev.filter((c) => c.key !== key));
  };

  // A slot still holding its column default counts as unset until someone picks a color.
  const isUnset = (entry: BrandColorEntry) => {
    const saved = savedColors.find((c) => c.id === entry.id);
    return isBlankHex(entry.hex_code) || (!!saved?.is_default && entry.hex_code.toUpperCase() === saved.hex_code.toUpperCase());
  };

  const swatch = (entry: BrandColorEntry, label: string) => (
    <div className="brand-swatch-well">
      <Form.Control
        type="color"
        aria-label={`${label} color`}
        value={HEX_RE.test(entry.hex_code) ? entry.hex_code : "#000000"}
        onChange={(e) => setField(entry.key, "hex_code", e.target.value)}
      />
    </div>
  );

  const hexInput = (entry: BrandColorEntry, label: string) => (
    <>
      <Form.Control
        type="text"
        className="brand-swatch-hex"
        aria-label={`${label} hex`}
        value={entry.hex_code}
        onChange={(e) => setField(entry.key, "hex_code", e.target.value)}
        placeholder="#000000"
      />
      {isInvalidHex(entry.hex_code) && (
        <div className="small text-warning mt-1">
          <i className="bi bi-exclamation-triangle me-1"></i>
          Not a valid hex color (#RRGGBB)
        </div>
      )}
      {!entry.id && isBlankHex(entry.hex_code) && <div className="small text-muted mt-1">Pick a color</div>}
    </>
  );

  return (
    <div className="brand-section">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h6 className="brand-section-title">Colors</h6>
          <p className="brand-section-hint">Selectable in Create for text, shapes and backgrounds.</p>
        </div>
        <span className="brand-section-count">
          {colors.length} {colors.length === 1 ? "color" : "colors"}
        </span>
      </div>

      <div className="brand-swatch-grid">
        {legacy.map((entry) => {
          const slot = LEGACY_SLOTS[entry.id] ?? { label: entry.category, required: false };
          return (
            <div key={entry.key} className="brand-swatch" role="group" aria-label={slot.label}>
              {swatch(entry, slot.label)}
              <div className="brand-swatch-body">
                <div className="brand-swatch-role mb-1 px-0">
                  {slot.label}
                  {slot.required && <span className="ms-1">*</span>}
                </div>
                {hexInput(entry, slot.label)}
                {slot.required && isUnset(entry) && <div className="small text-muted mt-1">Required</div>}
              </div>
            </div>
          );
        })}
      </div>

      <h6 className="brand-section-title mt-4">Additional Colors</h6>
      {rows.map((entry, i) => {
        const label = entry.name || `Color ${i + 1}`;
        return (
          <div key={entry.key} className="brand-swatch mt-3" role="group" aria-label={label}>
            <Row className="g-0">
              <Col md={2}>{swatch(entry, label)}</Col>
              <Col md={10}>
                <div className="brand-swatch-body border-top-0">
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-2" controlId={`color-${entry.key}-name`}>
                        <Form.Label className="small fw-semibold mb-1">Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={entry.name ?? ""}
                          onChange={(e) => setField(entry.key, "name", e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2" controlId={`color-${entry.key}-hex`}>
                        <Form.Label className="small fw-semibold mb-1">Hex</Form.Label>
                        {hexInput(entry, label)}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2" controlId={`color-${entry.key}-category`}>
                        <Form.Label className="small fw-semibold mb-1">Category</Form.Label>
                        <Form.Select
                          value={entry.category}
                          onChange={(e) => setField(entry.key, "category", e.target.value)}
                        >
                          {ROW_CATEGORIES.map(({ value, label }) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end justify-content-end mb-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        aria-label={`Remove ${label}`}
                        onClick={() => removeColor(entry.key)}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2" controlId={`color-${entry.key}-usage`}>
                        <Form.Label className="small fw-semibold mb-1">Usage Guidelines</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={entry.usage_guidelines ?? ""}
                          onChange={(e) => setField(entry.key, "usage_guidelines", e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2" controlId={`color-${entry.key}-application`}>
                        <Form.Label className="small fw-semibold mb-1">Application Guidelines</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={entry.application_guidelines ?? ""}
                          onChange={(e) => setField(entry.key, "application_guidelines", e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        );
      })}

      {cleared.map((field) => (
        <div key={field} className="small text-danger mt-3" role="alert">
          <i className="bi bi-exclamation-circle me-1"></i>
          The {field} can't be cleared once it's set.
        </div>
      ))}

      <div className="d-flex justify-content-between mt-3">
        <Button variant="outline-primary" size="sm" onClick={addColor} disabled={isLoading}>
          <i className="bi bi-plus-lg me-1"></i>
          Add Color
        </Button>
        <Button variant="primary" size="sm" onClick={() => onSave()} disabled={isLoading || cleared.length > 0}>
          Save Colors
        </Button>
      </div>
    </div>
  );
};

export default BrandColorsSection;
